"use client";
import { useEffect, useState } from "react";
import { SectionTitle } from "@/components/common/SectionTitle";
import { getAuthStatus } from "@/lib/auth";
import LoginRequiredToast from "@/components/common/LoginRequiredToast";
import { reportService } from "@/services/report.service";

const IMG_FALLBACK_LARGE = "/images/a8fb5af4afa8dd67e33d11d2c153c201224d8ca5.png";
const IMG_FALLBACK_SMALL = "/images/6f89d04c485512c83fafc054dd6e03c3136a7b6a.png";

const colors = {
  primary: "#0248fd",
  text: "#010103",
  textSecondary: "#65676e",
  textMuted: "#979ba5",
  white: "#ffffff",
};

interface ApiReportItem {
  id: number;
  title: string;
  summary?: string;
  createdAt?: string;
  keywords?: string[];
  thumbnailUrl?: string | null;
  viewCount?: number;
  similarityCount?: number;
}

function RankingNumber({ rank, size }: { rank: number; size: "large" | "small" }) {
  const sizeStyles = {
    large: "text-[36px] w-5 text-[#0248fd]",
    small: "text-[36px] w-[30px]",
  } as const;
  return (
    <div className={`font-['Pretendard:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 ${sizeStyles[size]}`}>
      <p className="leading-[normal]">{rank}</p>
    </div>
  );
}

function LargeReportCardContent({ item, isAuthed, onBlocked }: { item: ApiReportItem; isAuthed: boolean | null; onBlocked: () => void }) {
  return (
    <div className="content-stretch flex flex-col gap-4 items-start justify-start leading-[0] not-italic relative shrink-0 w-full">
      {isAuthed === true ? (
        <a href={`/reports/${item.id}`} className="font-['Pretendard:SemiBold',_sans-serif] relative shrink-0 text-[#010103] text-[24px] w-full hover:underline">
          <p className="leading-[1.4]">{item.title}</p>
        </a>
      ) : isAuthed === false ? (
        <a href="#" onClick={(e)=>{ e.preventDefault(); onBlocked(); }} className="font-['Pretendard:SemiBold',_sans-serif] relative shrink-0 text-[#010103] text-[24px] w-full">
          <p className="leading-[1.4]">{item.title}</p>
        </a>
      ) : (
        <div className="font-['Pretendard:SemiBold',_sans-serif] relative shrink-0 text-[#010103] text-[24px] w-full">
          <p className="leading-[1.4]">{item.title}</p>
        </div>
      )}
      <div className="-webkit-box basis-0 font-['Pretendard:Medium',_sans-serif] grow min-h-px min-w-px overflow-ellipsis overflow-hidden relative shrink-0 text-[#65676e] text-[16px] tracking-[-0.32px] w-full">
        <p className="leading-[1.5] line-clamp-3">{item.summary || ''}</p>
      </div>
      <div className="flex flex-col font-['Pretendard:Medium',_sans-serif] h-[22px] justify-end overflow-ellipsis overflow-hidden relative shrink-0 text-[#65676e] text-base lg:text-lg text-nowrap tracking-[-0.28px] w-full mt-1">
        <p className="[text-overflow:inherit] [text-wrap-mode:inherit] [white-space-collapse:inherit] leading-[normal] overflow-inherit">{item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : ''}</p>
      </div>
    </div>
  );
}

function LargeReportCard({ item, isAuthed, onBlocked }: { item: ApiReportItem; isAuthed: boolean | null; onBlocked: () => void }) {
  return (
    <div className="Frame1707482128 w-full lg:w-[660px] inline-flex flex-col justify-start items-start gap-5">
      <div className="Frame1707482127 self-stretch h-64 lg:h-80 lg:h-96 relative">
        {isAuthed === true ? (
          <a href={`/reports/${item.id}`} aria-label={item.title} className="Rectangle343492 w-full h-64 lg:h-80 lg:h-96 left-0 top-0 absolute rounded-2xl bg-center bg-cover bg-no-repeat block"
             style={{ backgroundImage: `url('${item.thumbnailUrl || IMG_FALLBACK_LARGE}')` }} />
        ) : isAuthed === false ? (
          <a href="#" onClick={(e)=>{ e.preventDefault(); onBlocked(); }} aria-label={item.title} className="Rectangle343492 w-full h-64 lg:h-80 lg:h-96 left-0 top-0 absolute rounded-2xl bg-center bg-cover bg-no-repeat block"
             style={{ backgroundImage: `url('${item.thumbnailUrl || IMG_FALLBACK_LARGE}')` }} />
        ) : (
          <div className="Rectangle343492 w-full h-64 lg:h-80 lg:h-96 left-0 top-0 absolute rounded-2xl bg-center bg-cover bg-no-repeat"
               style={{ backgroundImage: `url('${item.thumbnailUrl || IMG_FALLBACK_LARGE}')` }} />
        )}
        <div className="Frame1707482126 size-8 lg:size-10 lg:size-11 left-[12px] lg:left-[14px] lg:left-[16px] top-[12px] lg:top-[14px] lg:top-[16px] absolute flex items-center justify-center">
          <div className="Ellipse648 left-0 top-0 absolute">
            <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="22" cy="22" r="22" fill="#010103"/>
            </svg>
          </div>
          <div className="relative z-10 text-white text-xl lg:text-2xl lg:text-3xl font-semibold font-['Pretendard'] flex items-center justify-center w-full h-full">1</div>
        </div>
      </div>
      <div className="Frame2792 self-stretch px-1 flex flex-col justify-start items-start gap-4">
        <div className="Frame2791 self-stretch flex flex-col justify-start items-start gap-2">
          {isAuthed === true ? (
            <a href={`/reports/${item.id}`} className="self-stretch justify-start text-black text-2xl lg:text-2xl lg:text-3xl font-semibold font-['Pretendard'] leading-tight lg:leading-10 hover:underline">
              {item.title}
            </a>
          ) : isAuthed === false ? (
            <a href="#" onClick={(e)=>{ e.preventDefault(); onBlocked(); }} className="self-stretch justify-start text-black text-2xl lg:text-2xl lg:text-3xl font-semibold font-['Pretendard'] leading-tight lg:leading-10 hover:underline">
              {item.title}
            </a>
          ) : (
            <div className="self-stretch justify-start text-black text-2xl lg:text-2xl lg:text-3xl font-semibold font-['Pretendard'] leading-tight lg:leading-10">
              {item.title}
            </div>
          )}
          <div className="self-stretch justify-start text-neutral-500 text-lg lg:text-lg font-normal font-['Pretendard'] leading-relaxed line-clamp-3 lg:line-clamp-5">
            {item.summary || ''}
          </div>
        </div>
        <div className="718 self-stretch justify-start text-zinc-500 text-base lg:text-lg font-normal font-['Pretendard']">
          {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : ''}
        </div>
      </div>
    </div>
  );
}

function SmallReportCardContent({ item, isAuthed, onBlocked }: { item: ApiReportItem; isAuthed: boolean | null; onBlocked: () => void }) {
  return (
    <div className="content-stretch flex flex-col font-['Pretendard:Medium',_sans-serif] gap-2 items-start justify-start leading-[0] not-italic relative shrink-0 w-full">
      {isAuthed === true ? (
        <a href={`/reports/${item.id}`} className="relative shrink-0 text-[#010103] text-[16px] w-full hover:underline">
          <p className="leading-[1.35] line-clamp-2 font-semibold">{item.title}</p>
        </a>
      ) : isAuthed === false ? (
        <a href="#" onClick={(e)=>{ e.preventDefault(); onBlocked(); }} className="relative shrink-0 text-[#010103] text-[16px] w-full">
          <p className="leading-[1.35] line-clamp-2 font-semibold">{item.title}</p>
        </a>
      ) : (
        <div className="relative shrink-0 text-[#010103] text-[16px] w-full">
          <p className="leading-[1.35] line-clamp-2 font-semibold">{item.title}</p>
        </div>
      )}
      <div className="flex flex-col justify-end overflow-ellipsis overflow-hidden relative shrink-0 text-[#65676e] text-base lg:text-lg text-nowrap tracking-[-0.32px] w-full mt-1">
        <p className="[text-overflow:inherit] [text-wrap-mode:inherit] [white-space-collapse:inherit] leading-[normal] overflow-inherit">{item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : ''}</p>
      </div>
    </div>
  );
}

function SmallReportCard({ rank, item, isAuthed, onBlocked }: { rank: number; item: ApiReportItem; isAuthed: boolean | null; onBlocked: () => void }) {
  return (
    <div className="flex gap-3 lg:gap-4 lg:gap-6 items-start justify-start w-full h-auto lg:h-[125px]">
      {/* 왼쪽 번호 */}
      <div className="flex items-center justify-center w-6 h-6 lg:w-8 lg:h-8 shrink-0">
        <span className="text-black text-lg lg:text-2xl font-bold">{rank}</span>
      </div>
      
      {/* 중간 이미지 */}
      <div className="shrink-0 w-[120px] lg:w-[150px] lg:w-[205px] h-[80px] lg:h-[100px] lg:h-[125px] rounded-xl overflow-hidden">
        {isAuthed === true ? (
          <a href={`/reports/${item.id}`} aria-label={item.title} className="bg-center bg-cover bg-no-repeat w-full h-full block"
             style={{ backgroundImage: `url('${item.thumbnailUrl || IMG_FALLBACK_SMALL}')` }} />
        ) : isAuthed === false ? (
          <a href="#" onClick={(e)=>{ e.preventDefault(); onBlocked(); }} aria-label={item.title} className="bg-center bg-cover bg-no-repeat w-full h-full block"
             style={{ backgroundImage: `url('${item.thumbnailUrl || IMG_FALLBACK_SMALL}')` }} />
        ) : (
          <div className="bg-center bg-cover bg-no-repeat w-full h-full"
               style={{ backgroundImage: `url('${item.thumbnailUrl || IMG_FALLBACK_SMALL}')` }} />
        )}
      </div>
      
      {/* 오른쪽 텍스트 */}
      <div className="flex flex-col justify-center gap-1 grow min-w-0 h-full">
        {isAuthed === true ? (
          <a href={`/reports/${item.id}`} className="text-black text-lg lg:text-[20px] font-semibold hover:underline line-clamp-2 leading-tight">
            {item.title}
          </a>
        ) : isAuthed === false ? (
          <a href="#" onClick={(e)=>{ e.preventDefault(); onBlocked(); }} className="text-black text-lg lg:text-[20px] font-semibold hover:underline line-clamp-2 leading-tight">
            {item.title}
          </a>
        ) : (
          <div className="text-black text-lg lg:text-[20px] font-semibold line-clamp-2 leading-tight">
            {item.title}
          </div>
        )}
        <div className="text-gray-500 text-base lg:text-lg">
          {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : ''}
        </div>
      </div>
    </div>
  );
}

function SmallCardsGrid({ items, isAuthed, onBlocked }: { items: ApiReportItem[]; isAuthed: boolean | null; onBlocked: () => void }) {
  // console.log('[SmallCardsGrid] items:', items);
  return (
    <div className="content-stretch flex flex-col gap-6 items-start justify-start overflow-clip relative shrink-0 w-full">
      {items.slice(0, 4).map((it, idx) => (
        <SmallReportCard key={it.id} rank={idx + 2} item={it} isAuthed={isAuthed} onBlocked={onBlocked} />
      ))}
    </div>
  );
}

function ReportListArea({ items, isAuthed, onBlocked }: { items: ApiReportItem[]; isAuthed: boolean | null; onBlocked: () => void }) {
  const top1 = items[0];
  const next = items.slice(1);
  // console.log('[ReportListArea] items:', items);
  // console.log('[ReportListArea] top1:', top1);
  return (
    <div className="content-stretch flex flex-col lg:flex-row gap-6 lg:gap-[50px] items-start justify-start overflow-clip relative shrink-0 w-full">
      {top1 && (
        <div className="w-full lg:w-[660px]">
          <LargeReportCard item={top1} isAuthed={isAuthed} onBlocked={onBlocked} />
        </div>
      )}
      {next.length > 0 && (
        <div className="w-full lg:w-[850px]">
          <SmallCardsGrid items={next} isAuthed={isAuthed} onBlocked={onBlocked} />
        </div>
      )}
    </div>
  );
}

export function RankingReportSection({ blueTitle = "파란색제목", blackTitle = "검정색제목", showViewAll = true, titleHtml, viewAllHref }: { blueTitle?: string; blackTitle?: string; showViewAll?: boolean; titleHtml?: string; viewAllHref?: string }) {
  const [items, setItems] = useState<ApiReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState<boolean | null>(null);
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    setLoading(true);
    reportService.getTopViews(5)
      .then((response) => {
        // To-Be 형식 처리
        // top-views API는 data가 배열로 직접 반환됨
        const data = response.code === "SUCCESS" && Array.isArray(response.data)
          ? response.data.map((item, index) => ({
              id: item.id,
              title: item.title,
              summary: item.summary,
              createdAt: item.createdAt,
              keywords: item.keywords,
              thumbnailUrl: item.thumbnailUrl,
              viewCount: item.viewCount
            }))
          : [];
        setItems(data);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let mounted = true;
    getAuthStatus().then((d) => {
      if (!mounted) return;
      setIsAuthed(!!(d?.ok && d?.user));
    }).catch(() => { if (mounted) setIsAuthed(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="content-stretch flex flex-col gap-5 items-center justify-start relative size-full">
        <SectionTitle blueText={blueTitle} blackText={blackTitle} titleHtml={titleHtml} showViewAll={showViewAll} viewAllHref={viewAllHref} />
        <div className="flex items-center justify-center w-full h-[400px]">
          <div className="size-8 animate-spin rounded-full border-4 border-[#dadbe0] border-t-[#43464d]" aria-label="로딩 중" />
        </div>
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <div className="content-stretch flex flex-col gap-5 items-center justify-start relative size-full">
      <SectionTitle blueText={blueTitle} blackText={blackTitle} titleHtml={titleHtml} showViewAll={showViewAll} viewAllHref={viewAllHref} />
      <ReportListArea items={items} isAuthed={isAuthed} onBlocked={() => setToastVisible(true)} />
      <LoginRequiredToast isVisible={toastVisible} onClose={() => setToastVisible(false)} />
    </div>
  );
}

